import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDownward,
  Calculate,
  CheckCircle,
  HelpOutline,
  Lightbulb,
  ReportProblem,
} from '@mui/icons-material';
import styles from './DivisionPractice.module.css';

type InputName =
  | 'qT'
  | 'qO'
  | 'subT'
  | 'remT'
  | 'dropO'
  | 'subO'
  | 'remO'
  | 'finalQ'
  | 'finalR';

type ExpectedAnswers = {
  qT: number | '';
  subT: number | '';
  remT: number | '';
  dropO: number;
  qO: number;
  subO: number;
  remO: number;
  finalQ: number;
  finalR: number;
};

type DivisionProblem = {
  isTwoStep: boolean;
  divisor: number;
  hundreds: number;
  tens: number;
  ones: number;
  dividend: number;
  expected: ExpectedAnswers;
};

type GuessStatus = 'too_big' | 'too_small' | 'correct';

type Guess = {
  guess: number;
  result: number;
  message: string;
  status: GuessStatus;
};

type DigitRowProps = {
  value: string;
  expected: string;
  endColumn: number;
  row: number;
  active: boolean;
  onClick: () => void;
};

const MAX_GUESSES = 3;

const toHalfWidth = (value: string) =>
  value.replace(/[０-９]/g, (character) =>
    String.fromCharCode(character.charCodeAt(0) - 0xfee0),
  );

const generateProblem = (): DivisionProblem => {
  const divisor = Math.floor(Math.random() * 89) + 11;
  let dividend: number;
  let isTwoStep = Math.random() > 0.4;

  if (isTwoStep) {
    const firstTwo = Math.floor(Math.random() * (99 - divisor + 1)) + divisor;
    dividend = firstTwo * 10 + Math.floor(Math.random() * 10);
  } else {
    const firstTwo = Math.floor(Math.random() * (Math.max(10, divisor - 1) - 10 + 1)) + 10;
    dividend = firstTwo * 10 + Math.floor(Math.random() * 10);
  }

  const hundreds = Math.floor(dividend / 100);
  const tens = Math.floor((dividend % 100) / 10);
  const ones = dividend % 10;
  isTwoStep = hundreds * 10 + tens >= divisor;

  let qT: number | '' = '';
  let subT: number | '' = '';
  let remT: number | '' = '';
  let qO: number;
  let subO: number;
  let remO: number;

  if (isTwoStep) {
    const currentT = hundreds * 10 + tens;
    qT = Math.floor(currentT / divisor);
    subT = qT * divisor;
    remT = currentT - subT;
    const currentO = remT * 10 + ones;
    qO = Math.floor(currentO / divisor);
    subO = qO * divisor;
    remO = currentO - subO;
  } else {
    qO = Math.floor(dividend / divisor);
    subO = qO * divisor;
    remO = dividend - subO;
  }

  return {
    isTwoStep,
    divisor,
    hundreds,
    tens,
    ones,
    dividend,
    expected: {
      qT,
      subT,
      remT,
      dropO: ones,
      qO,
      subO,
      remO,
      finalQ: Math.floor(dividend / divisor),
      finalR: dividend % divisor,
    },
  };
};

function AnswerBox({
  value,
  active,
  correct,
  onClick,
}: {
  value: string;
  active: boolean;
  correct: boolean;
  onClick: () => void;
}) {
  const stateClass = active ? styles.answerBoxActive : correct ? styles.answerBoxCorrect : '';
  return (
    <button type="button" className={`${styles.answerBox} ${stateClass}`} onClick={onClick}>
      {value}
    </button>
  );
}

function DigitRow({ value, expected, endColumn, row, active, onClick }: DigitRowProps) {
  const startColumn = endColumn - expected.length + 1;
  return (
    <>
      {expected.split('').map((_, index) => (
        <div
          className={styles.gridCell}
          key={`${row}-${index}`}
          style={{ gridColumnStart: startColumn + index, gridRowStart: row }}
        >
          <AnswerBox
            value={value[index] || ''}
            active={active}
            correct={value === expected}
            onClick={onClick}
          />
        </div>
      ))}
    </>
  );
}

export function DivisionPractice() {
  const [problem, setProblem] = useState<DivisionProblem>(() => generateProblem());
  const [step, setStep] = useState(0);
  const [feedback, setFeedback] = useState('準備題目中……');
  const [selectedBox, setSelectedBox] = useState<InputName | null>(null);
  const [answers, setAnswers] = useState<Record<InputName, string>>({
    qT: '', qO: '', subT: '', remT: '', dropO: '',
    subO: '', remO: '', finalQ: '', finalR: '',
  });
  const [guessHistory, setGuessHistory] = useState<Guess[]>([]);
  const [guessCount, setGuessCount] = useState(0);

  const updateAnswer = useCallback((name: InputName, value: string) => {
    setAnswers((current) => ({ ...current, [name]: value }));
  }, []);

  const initNewProblem = useCallback(() => {
    setProblem(generateProblem());
    setAnswers({
      qT: '', qO: '', subT: '', remT: '', dropO: '',
      subO: '', remO: '', finalQ: '', finalR: '',
    });
    setGuessHistory([]);
    setGuessCount(0);
    setSelectedBox(null);
    setFeedback('準備題目中……');
    setStep(0);
  }, []);

  useEffect(() => {
    if (step !== 0) return;
    if (problem.isTwoStep) {
      setStep(1);
      setSelectedBox('qT');
      setFeedback(`計算 ${problem.dividend} ÷ ${problem.divisor}。先看前兩位，商是多少？`);
    } else {
      setStep(5);
      setSelectedBox('qO');
      setFeedback(`前兩位不夠除以 ${problem.divisor}，看全部三位。商是多少？`);
    }
  }, [problem, step]);

  useEffect(() => {
    if (step === 1 || step === 5) {
      setGuessHistory([]);
      setGuessCount(0);
    }
  }, [step]);

  useEffect(() => {
    const { expected, isTwoStep } = problem;

    if (isTwoStep && step === 1 && answers.qT === String(expected.qT)) {
      setStep(2);
      setSelectedBox('subT');
      setFeedback('商正確！現在請親自算出相乘結果。');
    } else if (isTwoStep && step === 2 && answers.subT === String(expected.subT)) {
      setStep(3);
      setSelectedBox('remT');
      setFeedback('正確！請填寫減法後的餘數。');
    } else if (isTwoStep && step === 3 && answers.remT === String(expected.remT)) {
      setStep(4);
      setSelectedBox('dropO');
      setFeedback(Number(expected.remT) > 0 ? '正確！請把個位數拉下來。' : '沒有餘數！請把個位數拉下來。');
    } else if (isTwoStep && step === 4 && answers.dropO === String(expected.dropO)) {
      setStep(5);
      setSelectedBox('qO');
      setFeedback('準備計算個位，這次的商是多少？');
    } else if (step === 5 && answers.qO === String(expected.qO)) {
      setStep(6);
      setSelectedBox('subO');
      setFeedback('商正確！現在請親自算出相乘結果。');
    } else if (step === 6 && answers.subO === String(expected.subO)) {
      setStep(7);
      setSelectedBox('remO');
      setFeedback('正確！請填寫最後的餘數。');
    } else if (step === 7 && answers.remO === String(expected.remO)) {
      setStep(8);
      setSelectedBox('finalQ');
      setFeedback('太棒了！直式完成，請填寫上面的橫式答案。');
    } else if (step === 8) {
      const hasRemainder = expected.finalR > 0;
      if (
        answers.finalQ === String(expected.finalQ) &&
        selectedBox === 'finalQ' &&
        hasRemainder
      ) {
        setSelectedBox('finalR');
      }
      const correct = hasRemainder
        ? answers.finalQ === String(expected.finalQ) && answers.finalR === String(expected.finalR)
        : answers.finalQ === String(expected.finalQ);
      if (correct) {
        setStep(9);
        setSelectedBox(null);
        setFeedback('恭喜你！全對了！🎉');
      }
    }
  }, [answers, problem, selectedBox, step]);

  const currentTarget = useMemo(() => {
    if (problem.isTwoStep && step <= 4) return problem.hundreds * 10 + problem.tens;
    if (problem.isTwoStep && step >= 5) return Number(problem.expected.remT) * 10 + problem.ones;
    return problem.dividend;
  }, [problem, step]);

  const currentCorrectQuotient = useMemo(() => {
    if (step === 1 && problem.isTwoStep) return Number(problem.expected.qT);
    if (step === 5) return problem.expected.qO;
    return null;
  }, [problem, step]);

  const isGuessStep = step === 1 || step === 5;
  const isLimitReached = guessCount >= MAX_GUESSES;
  const hasCorrectGuess = guessHistory.some((item) => item.status === 'correct');
  const hasRemainder = problem.expected.finalR > 0;
  const roundedDivisor = Math.round(problem.divisor / 10) * 10;

  const handleKeypadInput = (key: string) => {
    if (!selectedBox) return;
    const currentValue = answers[selectedBox];
    const expectedValue = String(problem.expected[selectedBox]);
    const expectedLength = expectedValue.length;

    if (key === 'CLEAR') {
      updateAnswer(selectedBox, '');
    } else if (key === 'DELETE') {
      updateAnswer(selectedBox, currentValue.slice(0, -1));
    } else if (currentValue.length < expectedLength) {
      updateAnswer(selectedBox, currentValue + key);
    }
  };

  const handleTestGuess = (guess: number) => {
    if (!isGuessStep || hasCorrectGuess || currentCorrectQuotient === null) return;
    if (isLimitReached && guess !== currentCorrectQuotient) return;
    if (guessHistory.some((item) => item.guess === guess)) return;

    const result = problem.divisor * guess;
    let status: GuessStatus;
    let message: string;
    if (result > currentTarget) {
      status = 'too_big';
      message = '太大 ❌';
    } else if (currentTarget - result >= problem.divisor) {
      status = 'too_small';
      message = '太小 ❌';
    } else {
      status = 'correct';
      message = '可能是它！✅';
    }

    setGuessHistory((history) => [{ guess, result, message, status }, ...history]);
    if (status !== 'correct') setGuessCount((count) => count + 1);
  };

  const selectBox = (name: InputName, availableFrom: number) => {
    if (step >= availableFrom && step < 9) setSelectedBox(name);
  };

  const handleDirectInput = (name: 'finalQ' | 'finalR', rawValue: string) => {
    const length = String(problem.expected[name]).length;
    updateAnswer(name, toHalfWidth(rawValue).replace(/[^0-9]/g, '').slice(0, length));
  };

  return (
    <main className={styles.page}>
      <div className={styles.pageInner}>
        <header className={styles.headerCard}>
          <div>
            <h1>小四除法練習</h1>
            <p>四捨五入試商法，動手找出正確的商！</p>
          </div>
          <div className={styles.horizontalAnswer}>
            <span>{problem.dividend} ÷ {problem.divisor} =</span>
            <input
              aria-label="橫式的商"
              className={selectedBox === 'finalQ' ? styles.finalInputActive : ''}
              disabled={step < 8}
              inputMode="numeric"
              onChange={(event) => handleDirectInput('finalQ', event.target.value)}
              onClick={() => selectBox('finalQ', 8)}
              placeholder={step >= 8 ? '商' : ''}
              value={answers.finalQ}
            />
            {hasRemainder && (
              <>
                <span className={styles.ellipsis}>……</span>
                <input
                  aria-label="橫式的餘數"
                  className={selectedBox === 'finalR' ? styles.finalInputActive : ''}
                  disabled={step < 8}
                  inputMode="numeric"
                  onChange={(event) => handleDirectInput('finalR', event.target.value)}
                  onClick={() => selectBox('finalR', 8)}
                  placeholder={step >= 8 ? '餘' : ''}
                  value={answers.finalR}
                />
              </>
            )}
          </div>
        </header>

        <div className={styles.columns}>
          <section className={styles.guessPanel}>
            <div className={styles.panelTitle}>
              <h2><Calculate />互動試商區</h2>
              {isGuessStep && !hasCorrectGuess && (
                <div className={styles.chances} aria-label={`尚餘 ${MAX_GUESSES - guessCount} 次提示機會`}>
                  {Array.from({ length: MAX_GUESSES }).map((_, index) => (
                    <span
                      key={index}
                      className={index < MAX_GUESSES - guessCount ? styles.chanceActive : ''}
                    />
                  ))}
                </div>
              )}
            </div>

            {isGuessStep ? (
              <>
                <div className={styles.hintCard}>
                  <strong>🎯 實際計算：<b>{currentTarget} ÷ {problem.divisor}</b></strong>
                  <div className={styles.hintText}>
                    <Lightbulb />
                    <span>
                      把 <b>{problem.divisor}</b> 當作 <em>{roundedDivisor}</em> 來估算：
                    </span>
                  </div>
                  <div className={styles.estimate}>
                    <small>估算式</small>
                    <b>{currentTarget} ÷ <em>{roundedDivisor}</em> = ?</b>
                  </div>
                </div>

                <div className={styles.guessInstruction}>
                  {hasCorrectGuess ? (
                    <span className={styles.successPill}><HelpOutline />找出商了！請親自計算相乘結果</span>
                  ) : isLimitReached ? (
                    <span className={styles.warningPill}><ReportProblem />請點擊發光的數字</span>
                  ) : (
                    <span>點擊下方數字試算：</span>
                  )}
                </div>

                <div className={styles.guessButtons}>
                  {Array.from({ length: 10 }).map((_, number) => {
                    const isCorrect = number === currentCorrectQuotient;
                    const guessed = guessHistory.some((item) => item.guess === number);
                    const disabled =
                      hasCorrectGuess ||
                      (isLimitReached && !isCorrect) ||
                      (!isLimitReached && guessed);
                    const stateClass = hasCorrectGuess
                      ? isCorrect ? styles.guessCorrect : styles.guessDisabled
                      : isLimitReached
                        ? isCorrect ? styles.guessReveal : styles.guessDisabled
                        : guessed ? styles.guessDisabled : '';
                    return (
                      <button
                        type="button"
                        key={number}
                        className={stateClass}
                        disabled={disabled}
                        onClick={() => handleTestGuess(number)}
                      >
                        {number}
                      </button>
                    );
                  })}
                </div>

                <div className={styles.guessHistory}>
                  {guessHistory.map((item) => (
                    <div className={styles[item.status]} key={item.guess}>
                      <span>
                        {problem.divisor} × {item.guess} =
                        {item.status === 'correct' ? <b className={styles.hiddenResult}>?</b> : <b>{item.result}</b>}
                      </span>
                      <strong>{item.message}</strong>
                    </div>
                  ))}
                  {guessHistory.length === 0 && <p>點擊上方數字，看看乘出來的結果！</p>}
                </div>
              </>
            ) : (
              <div className={styles.noGuess}>
                <CheckCircle />
                <span>{step >= 8 ? '計算完成！請準備進入下一題。' : '這一步不需要估算，請將結果填入右方直式。'}</span>
              </div>
            )}
          </section>

          <section className={styles.workArea}>
            <div className={styles.teacherHint}>
              <small>老師提示：</small>
              <p>
                {isLimitReached && !hasCorrectGuess && isGuessStep
                  ? '看來遇到瓶頸了！請點擊左方發光的正確數字。'
                  : feedback}
              </p>
            </div>

            <div className={styles.workspace}>
              <div className={`${styles.longDivisionCard} ${hasCorrectGuess && isGuessStep ? styles.readyToFill : ''}`}>
                <div className={styles.divisionGrid}>
                  {problem.isTwoStep && (
                    <div className={styles.gridCell} style={{ gridColumnStart: 5, gridRowStart: 1 }}>
                      <AnswerBox
                        value={answers.qT}
                        active={step === 1}
                        correct={answers.qT === String(problem.expected.qT)}
                        onClick={() => selectBox('qT', 1)}
                      />
                    </div>
                  )}
                  <div className={styles.gridCell} style={{ gridColumnStart: 6, gridRowStart: 1 }}>
                    <AnswerBox
                      value={answers.qO}
                      active={step === 5}
                      correct={answers.qO === String(problem.expected.qO)}
                      onClick={() => selectBox('qO', 5)}
                    />
                  </div>

                  <div className={styles.printedDigit} style={{ gridColumnStart: 1, gridRowStart: 2 }}>{Math.floor(problem.divisor / 10)}</div>
                  <div className={styles.printedDigit} style={{ gridColumnStart: 2, gridRowStart: 2 }}>{problem.divisor % 10}</div>
                  <div className={styles.divisionCorner} style={{ gridColumnStart: 3, gridRowStart: 2 }} />
                  {[problem.hundreds, problem.tens, problem.ones].map((digit, index) => (
                    <div
                      className={`${styles.printedDigit} ${styles.dividendDigit}`}
                      key={index}
                      style={{ gridColumnStart: index + 4, gridRowStart: 2 }}
                    >
                      {digit}
                    </div>
                  ))}

                  {problem.isTwoStep && (
                    <>
                      {step >= 2 && (
                        <DigitRow
                          value={answers.subT}
                          expected={String(problem.expected.subT)}
                          endColumn={5}
                          row={3}
                          active={step === 2}
                          onClick={() => selectBox('subT', 2)}
                        />
                      )}
                      {step >= 3 && <div className={styles.divisionLine} style={{ gridRowStart: 4 }} />}
                      {step >= 3 && (
                        <DigitRow
                          value={answers.remT}
                          expected={String(problem.expected.remT)}
                          endColumn={5}
                          row={5}
                          active={step === 3}
                          onClick={() => selectBox('remT', 3)}
                        />
                      )}
                      {step >= 4 && (
                        <div className={styles.dropCell} style={{ gridColumnStart: 6, gridRowStart: 5 }}>
                          <AnswerBox
                            value={answers.dropO}
                            active={step === 4}
                            correct={answers.dropO === String(problem.expected.dropO)}
                            onClick={() => selectBox('dropO', 4)}
                          />
                          {step === 4 && !answers.dropO && <ArrowDownward />}
                        </div>
                      )}
                    </>
                  )}

                  {step >= 6 && (
                    <DigitRow
                      value={answers.subO}
                      expected={String(problem.expected.subO)}
                      endColumn={6}
                      row={problem.isTwoStep ? 6 : 3}
                      active={step === 6}
                      onClick={() => selectBox('subO', 6)}
                    />
                  )}
                  {step >= 7 && (
                    <div className={styles.divisionLine} style={{ gridRowStart: problem.isTwoStep ? 7 : 4 }} />
                  )}
                  {step >= 7 && (
                    <DigitRow
                      value={answers.remO}
                      expected={String(problem.expected.remO)}
                      endColumn={6}
                      row={problem.isTwoStep ? 8 : 5}
                      active={step === 7}
                      onClick={() => selectBox('remO', 7)}
                    />
                  )}
                </div>
              </div>

              {step < 9 ? (
                <div className={styles.keypad}>
                  {isLimitReached && !hasCorrectGuess && isGuessStep && (
                    <div className={styles.keypadLock}><span>請先點擊左方發光數字</span></div>
                  )}
                  <p>點擊數字填入直式</p>
                  <div>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((number) => (
                      <button type="button" key={number} onClick={() => handleKeypadInput(String(number))}>
                        {number}
                      </button>
                    ))}
                    <button type="button" className={styles.clearKey} onClick={() => handleKeypadInput('CLEAR')}>清除</button>
                    <button type="button" onClick={() => handleKeypadInput('0')}>0</button>
                    <button type="button" className={styles.deleteKey} onClick={() => handleKeypadInput('DELETE')}>刪除</button>
                  </div>
                </div>
              ) : (
                <div className={styles.nextArea}>
                  <button type="button" onClick={initNewProblem}><CheckCircle />下一題</button>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}

